import type { DataTableColumn, DataTableColumnState, DataTableColumnToggle } from "./tables";

type DataTableColumnWidth = Record<string, string | number>;

/** The three column-state slices useDatatableColumns persists under `${key}-columns-order|toggle|width`. */
export interface PersistedDataTableColumnState extends DataTableColumnState {
  columnsWidth: DataTableColumnWidth[];
}

const hasSameOrder = (left: string[], right: string[]) =>
  left.length === right.length && left.every((accessor, index) => accessor === right[index]);

const hasSameToggleState = (left: DataTableColumnToggle[], right: DataTableColumnToggle[]) =>
  left.length === right.length && left.every((toggle, index) => toggle.accessor === right[index]?.accessor);

/**
 * The state Mantine 6's useDataTableColumns seeded before anything was stored. mantine-datatable 7's hook
 * prunes stored state on every render, so SC-2703 moved its seeding here to keep reconciliation append-only.
 */
export const defaultDataTableColumnState = <T>(
  columns: Pick<DataTableColumn<T>, "accessor" | "defaultToggle" | "toggleable" | "width">[],
): PersistedDataTableColumnState => ({
  columnsOrder: columns.map(column => column.accessor),
  columnsToggle: columns.map(column => ({
    accessor: column.accessor,
    // Mantine 6 seeded `defaultToggle || true`, which is always true; the stored entries keep that value.
    defaultToggle: true,
    // Mantine 6 left this undefined (absent from the stored JSON) for a definition without it. The owned
    // entry type is boolean and every reader, reconciliation included, treats absent and false alike.
    toggleable: column.toggleable ?? false,
    toggled: column.defaultToggle ?? true,
  })),
  columnsWidth: columns.map(column => ({ [column.accessor]: column.width ?? "initial" })),
});

/**
 * Mantine 6's effective columns: stored order, visibility from the stored toggle (overriding a definition's
 * own `hidden`), and the stored width (replacing a definition's width, with nothing when no width is
 * stored for it). A stored accessor with no definition yields no column; Mantine 6 emitted an inert
 * hidden entry without an accessor in its place, which the table never rendered.
 */
export const effectiveDataTableColumns = <T>(
  columns: DataTableColumn<T>[],
  { columnsOrder, columnsToggle, columnsWidth }: PersistedDataTableColumnState,
): DataTableColumn<T>[] =>
  columnsOrder.flatMap(accessor => {
    const column = columns.find(candidate => candidate.accessor === accessor);
    if (column === undefined) {
      return [];
    }
    return [
      {
        ...column,
        hidden: !columnsToggle.find(toggle => toggle.accessor === accessor)?.toggled,
        width: columnsWidth.find(width => width[accessor])?.[accessor],
      },
    ];
  });

export const reconcileDataTableColumnState = <T>(
  columns: Pick<DataTableColumn<T>, "accessor" | "defaultToggle" | "toggleable">[],
  state: DataTableColumnState,
): DataTableColumnState => {
  const orderedAccessors = new Set<string>();
  const reconciledColumnsOrder: string[] = [];

  state.columnsOrder.forEach(accessor => {
    if (!orderedAccessors.has(accessor)) {
      orderedAccessors.add(accessor);
      reconciledColumnsOrder.push(accessor);
    }
  });

  columns.forEach(column => {
    if (!orderedAccessors.has(column.accessor)) {
      orderedAccessors.add(column.accessor);
      reconciledColumnsOrder.push(column.accessor);
    }
  });

  const toggledAccessors = new Set<string>();
  const reconciledColumnsToggle: DataTableColumnToggle[] = [];

  state.columnsToggle.forEach(toggle => {
    if (!toggledAccessors.has(toggle.accessor)) {
      toggledAccessors.add(toggle.accessor);
      reconciledColumnsToggle.push(toggle);
    }
  });

  columns.forEach(column => {
    if (toggledAccessors.has(column.accessor)) {
      return;
    }
    const defaultToggle = column.defaultToggle ?? true;
    toggledAccessors.add(column.accessor);
    reconciledColumnsToggle.push({
      accessor: column.accessor,
      defaultToggle,
      toggleable: column.toggleable ?? false,
      toggled: defaultToggle,
    });
  });

  return {
    columnsOrder: hasSameOrder(state.columnsOrder, reconciledColumnsOrder) ? state.columnsOrder : reconciledColumnsOrder,
    columnsToggle: hasSameToggleState(state.columnsToggle, reconciledColumnsToggle) ? state.columnsToggle : reconciledColumnsToggle,
  };
};
